import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, ChevronDown, ChevronUp, HelpCircle, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const HelpCenterPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          id: 'gs1',
          question: 'How do I create an account on Shoot Base?',
          answer: 'Click the "Join Now" button on the homepage, choose whether you\'re a professional or client, fill in your details including name, email, and password, and verify your email address. Once verified, you can complete your profile.'
        },
        {
          id: 'gs2',
          question: 'Is Shoot Base free to use?',
          answer: 'Yes, creating a profile and browsing professionals is completely free. We may introduce premium features in the future for enhanced visibility and additional tools.'
        },
        {
          id: 'gs3',
          question: 'How do I get verified?',
          answer: 'After creating your profile, submit your credentials, portfolio, and work history. Our team reviews submissions within 3-5 business days. Verified professionals receive a badge on their profile.'
        }
      ]
    },
    {
      category: 'For Professionals',
      questions: [
        {
          id: 'pro1',
          question: 'How do I make my profile stand out?',
          answer: 'Upload a professional photo, add detailed work experience, showcase your best portfolio pieces, include client testimonials, keep your profile updated, and respond promptly to inquiries.'
        },
        {
          id: 'pro2',
          question: 'Can I list multiple specializations?',
          answer: 'Yes! You can select multiple departments and specializations. This increases your visibility in searches and helps you get discovered for diverse projects.'
        },
        {
          id: 'pro3',
          question: 'How do clients contact me?',
          answer: 'Clients can reach you through the platform\'s messaging system or view your contact details (if you\'ve chosen to make them public). You\'ll receive notifications for all inquiries.'
        },
        {
          id: 'pro4',
          question: 'Can I update my rates and availability?',
          answer: 'Absolutely. You can update your daily rates, availability calendar, and project preferences anytime from your profile settings.'
        }
      ]
    },
    {
      category: 'For Clients',
      questions: [
        {
          id: 'client1',
          question: 'How do I post a hiring request?',
          answer: 'Click "Create a Hiring Post", fill in project details including departments needed, roles, locations, timeline, and budget (optional). Your post goes live after a quick review.'
        },
        {
          id: 'client2',
          question: 'Can I hire crew for multiple locations?',
          answer: 'Yes! When creating a hiring post, you can add multiple locations and specify timelines for each. This helps you build crews for multi-city shoots efficiently.'
        },
        {
          id: 'client3',
          question: 'How do I know if a professional is reliable?',
          answer: 'Look for the "Verified" badge, check their portfolio and past projects, read reviews from other clients, and review their experience level and specializations.'
        },
        {
          id: 'client4',
          question: 'Can I hire an entire crew at once?',
          answer: 'Yes! You can select multiple departments in a single hiring post (e.g., Camera, Sound, Lighting) and hire a complete crew for your project.'
        }
      ]
    },
    {
      category: 'Account & Profile',
      questions: [
        {
          id: 'acc1',
          question: 'How do I edit my profile?',
          answer: 'Log in, go to your profile page, and click the "Edit Profile" button. You can update your details, add portfolio items, and change your settings anytime.'
        },
        {
          id: 'acc2',
          question: 'Can I delete my account?',
          answer: 'Yes, you can delete your account from the Account Settings page. Note that this action is permanent and all your data will be removed.'
        },
        {
          id: 'acc3',
          question: 'I forgot my password. What should I do?',
          answer: 'Click "Forgot Password" on the login page, enter your registered email, and you\'ll receive a password reset link. Follow the instructions to set a new password.'
        },
        {
          id: 'acc4',
          question: 'How do I change my email address?',
          answer: 'Go to Account Settings, update your email address, and verify the new email through the confirmation link sent to your inbox.'
        }
      ]
    },
    {
      category: 'Payments & Pricing',
      questions: [
        {
          id: 'pay1',
          question: 'Does Shoot Base handle payments?',
          answer: 'Currently, Shoot Base is a connection platform. Payment terms are negotiated directly between clients and professionals. We may introduce payment handling in the future.'
        },
        {
          id: 'pay2',
          question: 'Are there any commission fees?',
          answer: 'No, Shoot Base does not charge commission on projects. Our platform is free for connecting professionals and clients.'
        },
        {
          id: 'pay3',
          question: 'What if there\'s a payment dispute?',
          answer: 'Since payments are handled directly, we recommend clear written agreements before starting work. For disputes, you can contact our support team for mediation guidance.'
        }
      ]
    }
  ];

  const filteredFaqs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const toggleQuestion = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-8"
          >
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
              <HelpCircle className="w-3 h-3 mr-1" />
              Help Center
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              How Can We
              <span className="text-accent"> Help You?</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              Find answers to frequently asked questions about using Shoot Base
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 py-6 text-base bg-card border-border"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            {filteredFaqs.map((category, catIndex) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: catIndex * 0.1 }}
              >
                <h2 className="text-2xl font-bold mb-4 text-foreground">{category.category}</h2>
                <div className="space-y-3">
                  {category.questions.map((faq) => (
                    <Card key={faq.id} className="bg-card border-border overflow-hidden">
                      <CardContent className="p-0">
                        <button
                          onClick={() => toggleQuestion(faq.id)}
                          className="w-full px-6 py-4 flex items-center justify-between hover:bg-accent/5 transition-colors text-left"
                        >
                          <span className="font-medium text-foreground pr-4">{faq.question}</span>
                          {expandedId === faq.id ? (
                            <ChevronUp className="w-5 h-5 text-accent flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          )}
                        </button>
                        {expandedId === faq.id && (
                          <div className="px-6 pb-4 pt-2 text-muted-foreground text-sm leading-relaxed border-t border-border">
                            {faq.answer}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>
            ))}

            {filteredFaqs.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg mb-4">No results found for "{searchQuery}"</p>
                <p className="text-muted-foreground text-sm">Try different keywords or browse all categories above</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <Mail className="w-12 h-12 text-accent mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-3 text-foreground">Still Need Help?</h2>
            <p className="text-muted-foreground mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <Button 
              size="lg" 
              className="bg-accent hover:bg-accent/90 text-primary-foreground font-semibold"
              onClick={() => navigate('/contact')}
            >
              Contact Support
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenterPage;